import { Res } from '@cbo/shared-library';
import { CurrencyFormat } from '../../models/config';
import { Regions, TRegion } from '../../config';

/**
 * Returns the currency format, combining regional preferences with the user's saved preferences
 * @param preferences
 * @returns Returns an instance of CurrencyFormat that includes locale and format options
 */
export const getCurrencyFormat = (preferences?: Res.Admin.UserPreferences | null): CurrencyFormat => {
  const region: TRegion = (preferences?.region as TRegion) || 'en-US';
  const locale: string = Regions[region].locale || region;
  return { ...Regions[region].formatting.currency, locale };
};

export const getCurrencySymbol = (preferences?: Res.Admin.UserPreferences | null): string => {
  const currencyFormat = getCurrencyFormat(preferences);
  const formatter = new Intl.NumberFormat(currencyFormat.locale, {
    style: 'currency',
    currency: currencyFormat.options?.currency,
  });

  const parts = formatter.formatToParts(1);
  const currencySymbol = parts.find((part) => part.type === 'currency');

  return currencySymbol ? currencySymbol.value : '';
};

/**
 * Formats currency based on regional preferences and the user's saved preferences
 * @param value
 * @param preferences
 * @param format
 * @param alwaysIncludeSigns - If true, prefix positive values with a 'plus'
 * sign in addition to default behavior (prefix negative values with a 'minus'
 * sign).
 * @returns Returns the specified currency (value) as formatted string
 */
export const formatCurrency = (
  value: number,
  preferences?: Res.Admin.UserPreferences | null,
  format?: CurrencyFormat,
  useCompact?: boolean,
  alwaysIncludeSigns?: boolean,
  maxDigits = 2
): string => {
  const { options, locale } = format || getCurrencyFormat(preferences);
  const opts: Intl.NumberFormatOptions = {
    ...(options as Intl.NumberFormatOptions),
    style: 'currency',
    signDisplay: alwaysIncludeSigns ? 'exceptZero' : 'auto',
  };

  // This function is called with a string for `value` in some parts of the app.
  // Data that populate some "currency" column in a datagrid are common
  // examples. These instances are super low-priority to hunt down and fix, so
  // in the meantime, if we recognize at runtime that we've been provided a
  // string for `value`, optimistically assume that it's a valid number, and
  // convert it to one.
  const numericValue = typeof value === 'string' ? Number(value) : value;
  // Check that the rounded value with `maxDigits` fraction digits is not
  // exactly 0.00... `Intl.NumberFormat()` decides what sign to include based on
  // the raw value provided -- not the value after rounding.
  //
  // For instance, if `value` = -0.002375, and `maxDigits` = 2, then without
  // performing this check, `formatCurrency()` would return '-$0.00' (or the
  // international equivalent).
  const roundedValue = Number.parseFloat(numericValue.toFixed(maxDigits));
  const verifiedValue = roundedValue === 0 ? 0 : value;

  const formatter = new Intl.NumberFormat(locale, {
    ...opts,
    ...(useCompact && { notation: 'compact', maximumFractionDigits: maxDigits }),
  });

  const shouldHideCurrencySymbol = preferences?.showCurrencySymbol === false;
  if (shouldHideCurrencySymbol) {
    return formatter
      .formatToParts(verifiedValue)
      .filter((x) => x.type !== 'currency')
      .map((x) => x.value)
      .join('')
      .trim();
  }
  return formatter.format(verifiedValue);
};
